import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { useParams, useHistory } from "react-router-dom";
import { Grid } from "@material-ui/core";
import {
  FaUsers,
  FaUserCheck,
  FaUserClock,
  FaClock,
  FaMapMarkedAlt,
} from "react-icons/fa";
import axios from "axios";
import BackButton from "../../components/BackButton";
import { toast, ToastContainer } from "react-toastify";
import { format, differenceInMinutes } from "date-fns";
import MDSpinner from "react-md-spinner";

import myConfig from "../../configs/config";
import AttendanceKpiCard from "./Components/AttendanceKpiCard";
import WorkerStatusBadge from "./Components/WorkerStatusBadge";
import AttendanceFilters from "./Components/AttendanceFilters";
import ExportButton from "../../components/Export_button";

import "./AttendanceDashboard.css";

function AttendanceDashboard(props) {
  const params = useParams();
  const history = useHistory();
  const farmId = params.farm_id;

  const [workers, setWorkers] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");

  // KPI States
  const [totalWorkers, setTotalWorkers] = useState(0);
  const [clockedInCount, setClockedInCount] = useState(0);
  const [clockedOutCount, setClockedOutCount] = useState(0);
  const [averageHours, setAverageHours] = useState(0);

  // Fetch workers with profiles
  const fetchWorkers = useCallback(async () => {
    try {
      const WORKER_PROFILE_URL = `${myConfig.BE_URL}/api/attendance/worker-profiles/?farm_id=${farmId}&is_active=true`;

      const response = await axios({
        baseURL: WORKER_PROFILE_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      });

      const data = response.data;
      setWorkers(Array.isArray(data) ? data : []);
      setTotalWorkers(Array.isArray(data) ? data.length : 0);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching workers:", error);
      toast.error(props.t("Falha ao carregar trabalhadores"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [farmId, props]);

  // Fetch attendance records
  const fetchAttendanceRecords = useCallback(async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const ATTENDANCE_HISTORY_URL = `${myConfig.BE_URL}/api/attendance/records/?farm_id=${farmId}&is_active=true`;

      const response = await axios({
        baseURL: ATTENDANCE_HISTORY_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
          "Content-Type": "application/json",
        },
        data: {
          from_date: today,
          to_date: today,
        },
      });

      const data = response.data;
      const records = Array.isArray(data) ? data : data.workers || [];
      setAttendanceRecords(records);

      // Calculate KPIs
      const clockedIn = records.filter(
        (r) =>
          r.action === "clock_in" &&
          !records.find(
            (r2) =>
              r2.worker_profile === r.worker_profile &&
              r2.action === "clock_out" &&
              new Date(r2.timestamp) > new Date(r.timestamp)
          )
      ).length;

      setClockedInCount(clockedIn);
      setClockedOutCount(totalWorkers - clockedIn);

      // Calculate average hours for workers who clocked out today
      const completedShifts = records.filter((r) => r.action === "clock_out");
      if (completedShifts.length > 0) {
        const totalHours = completedShifts.reduce((sum, record) => {
          const clockInRecord = records.find(
            (r) =>
              r.worker_profile === record.worker_profile &&
              r.action === "clock_in" &&
              new Date(r.timestamp) < new Date(record.timestamp)
          );
          if (clockInRecord) {
            const duration = differenceInMinutes(
              new Date(record.timestamp),
              new Date(clockInRecord.timestamp)
            );
            return sum + duration / 60;
          }
          return sum;
        }, 0);
        setAverageHours(totalHours / completedShifts.length);
      } else {
        setAverageHours(0);
      }

      setLastUpdate(new Date());
      setLoading(false);
    } catch (error) {
      console.error("Error fetching attendance records:", error);
      toast.error(props.t("Falha ao carregar registros de ponto"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
    }
  }, [farmId, totalWorkers, props]);

  // Initial load
  useEffect(() => {
    document.title = "Agriconnected - Painel de Ponto";
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    if (workers.length > 0) {
      fetchAttendanceRecords();
    }
  }, [workers, fetchAttendanceRecords]);

  // 30-second polling
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAttendanceRecords();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [fetchAttendanceRecords]);

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Get current status for worker
  const getWorkerCurrentStatus = (workerId) => {
    const workerRecords = attendanceRecords
      .filter((r) => r.worker_profile.operator_id === workerId)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    if (workerRecords.length === 0) return { status: "clocked_out" };

    const lastRecord = workerRecords[0].records[0];
    const clockInRecord = workerRecords[0].records.find(
      (r) => r.action === "clock_in"
    );
    console.log("workerRecords", workerRecords, lastRecord, clockInRecord);

    return {
      status: lastRecord.action === "clock_in" ? "clocked_in" : "clocked_out",
      lastAction: lastRecord.action,
      lastTimestamp: lastRecord.timestamp,
      clockInTime:
        lastRecord.action === "clock_in"
          ? lastRecord.timestamp
          : clockInRecord
          ? clockInRecord.timestamp
          : null,
      clockOutTime:
        lastRecord.action === "clock_out" ? lastRecord.timestamp : null,
      latitude: lastRecord.location_latitude,
      longitude: lastRecord.location_longitude,
    };
  };

  // Calculate work duration
  const calculateDuration = (clockInTime, clockOutTime) => {
    console.log("calculateDuration", clockInTime, clockOutTime);
    if (!clockInTime) return "-";

    const endTime = clockOutTime ? new Date(clockOutTime) : new Date();
    const startTime = new Date(clockInTime);
    const durationMinutes = differenceInMinutes(endTime, startTime);

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    return `${hours}h ${minutes}m`;
  };

  // Filter workers
  const getFilteredWorkers = () => {
    return workers.filter((worker) => {
      console.log("Filtering worker", worker);
      const status = getWorkerCurrentStatus(worker.operator_id);
      console.log("Worker status for filtering", status);
      // Status filter
      if (statusFilter !== "all") {
        if (statusFilter === "clocked_in" && status.status !== "clocked_in")
          return false;
        if (statusFilter === "clocked_out" && status.status !== "clocked_out")
          return false;
      }

      // Search filter
      if (searchDebounce) {
        const searchLower = searchDebounce.toLowerCase();
        const workerName = (worker.operator_name || "").toLowerCase();
        const cpf = worker.cpf || "";

        return workerName.includes(searchLower) || cpf.includes(searchLower);
      }

      return true;
    });
  };

  const filteredWorkers = getFilteredWorkers();

  const handleViewOnMap = (workerId) => {
    history.push(`/${farmId}/operator/attendance/map?worker=${workerId}`);
  };

  return (
    <div id="attendance_dashboard_container">
      <div className="x_title">
        <BackButton to={`/${farmId}/operator`} label={props.t("Voltar")} />
        <h2>{props.t("Painel de Ponto")}</h2>
        <div className="clearfix"></div>
      </div>

      {loading ? (
        <div
          style={{ marginTop: "50px", marginLeft: "50%", minHeight: "200px" }}
        >
          <MDSpinner />
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={3} style={{ marginBottom: "30px" }}>
            <Grid item xs={12} sm={6} md={3}>
              <AttendanceKpiCard
                title={props.t("Trabalhadores Registrados")}
                value={totalWorkers}
                icon={<FaUsers />}
                color="#007bff"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AttendanceKpiCard
                title={props.t("Registrados Agora")}
                value={clockedInCount}
                icon={<FaUserCheck />}
                color="#28a745"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AttendanceKpiCard
                title={props.t("Fora de Expediente")}
                value={clockedOutCount}
                icon={<FaUserClock />}
                color="#6c757d"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <AttendanceKpiCard
                title={props.t("Horas Médias Hoje")}
                value={averageHours.toFixed(1)}
                icon={<FaClock />}
                color="#ffc107"
              />
            </Grid>
          </Grid>

          {/* Filters */}
          <AttendanceFilters
            statusFilter={statusFilter}
            onStatusFilterChange={(e) => setStatusFilter(e.target.value)}
            searchTerm={searchTerm}
            onSearchChange={(e) => setSearchTerm(e.target.value)}
            showDateRange={false}
            t={props.t}
          />

          {/* Last Update Info */}
          <div className="last-update-info">
            <span>
              {props.t("Última Atualização")}:{" "}
              {format(lastUpdate, "dd/MM/yyyy HH:mm:ss")}
            </span>
            <button
              className="btn btn-sm btn-primary"
              onClick={fetchAttendanceRecords}
              style={{ marginLeft: "15px" }}
            >
              {props.t("Atualizar Agora")}
            </button>
          </div>

          {/* Attendance Table */}
          <div className="attendance-table-container">
            <table className="table table-hover table-striped">
              <thead>
                <tr>
                  <th>{props.t("Nome")}</th>
                  <th>{props.t("CPF")}</th>
                  <th>{props.t("Data e Hora de Entrada")}</th>
                  <th>{props.t("Data e Hora de Saída")}</th>
                  <th>{props.t("Duração do Expediente")}</th>
                  <th>{props.t("Localização")}</th>
                  <th>{props.t("Status")}</th>
                  <th>{props.t("Ações")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkers.length === 0 ? (
                  <tr>
                    <td colSpan="8" align="center">
                      <h4>{props.t("Nenhum trabalhador encontrado")}</h4>
                    </td>
                  </tr>
                ) : (
                  filteredWorkers.map((worker) => {
                    const status = getWorkerCurrentStatus(worker.operator_id);
                    console.log("status", status);
                    return (
                      <tr key={worker.id}>
                        <td>{worker.operator_name}</td>
                        <td>{worker.cpf || "-"}</td>
                        <td>
                          {status.clockInTime
                            ? format(
                                new Date(status.clockInTime),
                                "dd/MM/yyyy HH:mm:ss"
                              )
                            : "-"}
                        </td>
                        <td>
                          {status.clockOutTime
                            ? format(
                                new Date(status.clockOutTime),
                                "dd/MM/yyyy HH:mm:ss"
                              )
                            : "-"}
                        </td>
                        <td>
                          {calculateDuration(
                            status.clockInTime,
                            status.clockOutTime
                          )}
                        </td>
                        <td>
                          {status.latitude && status.longitude ? (
                            <a
                              href={`https://www.google.com/maps?q=${status.latitude},${status.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "#007bff" }}
                            >
                              {status.latitude}, {status.longitude}
                            </a>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <WorkerStatusBadge status={status.status} />
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-info"
                            onClick={() => handleViewOnMap(worker.operator_id)}
                            title={props.t("Ver no Mapa")}
                          >
                            <FaMapMarkedAlt />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Export Button */}
          <div style={{ marginTop: "20px", textAlign: "center" }}>
            <ExportButton
              classname="btn btn-default-export"
              export={`${
                myConfig.BE_URL
              }/api/attendance/export/?farm_id=${farmId}&date=${format(
                new Date(),
                "yyyy-MM-dd"
              )}`}
            />
          </div>
        </>
      )}

      <ToastContainer />
    </div>
  );
}

export default withTranslation()(AttendanceDashboard);
